/**
 * Display and comparison helpers for Benny agent rules on the verification form.
 */

import type { FieldValue, RulePredicate } from "./fieldDependencyManager";

export type AppliedRuleStatus = "applied" | "overridden" | "advisory";

export interface RuleCondition {
  conditions: RulePredicate[];
}

export interface AppliedRuleDisplay {
  field: string;
  label: string;
  value: FieldValue;
  note: string | null;
  status: AppliedRuleStatus;
  condition: string | null;
}

const RULE_FIELD_LABELS: Record<string, string> = {
  groupAcceptsAssignmentOfBenefits: "Assignment of Benefits",
  preAuthorizationRequired: "Pre-Authorization Required",
  orthoInitialPaymentPercentage: "Initial Payment Percentage",
  orthoManualOrAutomaticPayments: "Manual or Automatic Payments",
  orthoPaymentFrequency: "Payment Frequency",
};

export const ruleFieldVisibilityKey = (field: string): string =>
  field.replace(/([A-Z])/g, "_$1").toLowerCase();

export const ruleFieldLabel = (field: string): string =>
  RULE_FIELD_LABELS[field] ??
  field
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

export const formatRuleValue = (value: FieldValue): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") {
    return value
      .split("_")
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" ");
  }
  return String(value);
};

const CONDITION_LABELS: Record<string, string> = {
  eq: "is",
  ne: "is not",
  gt: "is greater than",
  gte: "is at least",
  lt: "is less than",
  lte: "is at most",
  is_null: "is empty",
  is_not_null: "is set",
};

const formatPredicate = (predicate: RulePredicate): string | null => {
  if (predicate.condition === "always" || !predicate.dependsOnField) {
    return null;
  }
  const label = ruleFieldLabel(predicate.dependsOnField);
  const op = CONDITION_LABELS[predicate.condition] ?? predicate.condition;
  if (
    predicate.condition === "is_null" ||
    predicate.condition === "is_not_null"
  ) {
    return `${label} ${op}`;
  }
  return `${label} ${op} ${formatRuleValue(predicate.dependsOnValue)}`;
};

export const formatRuleCondition = (
  ruleCondition: RuleCondition | undefined,
): string | null => {
  const parts = (ruleCondition?.conditions ?? [])
    .map(formatPredicate)
    .filter((p): p is string => Boolean(p));
  if (parts.length === 0) return null;
  return `When ${parts.join(" and ")}`;
};

// Form fields hold numbers as either numbers or strings depending on entry path.
export const ruleValueMatches = (
  current: FieldValue,
  ruleValue: FieldValue,
): boolean => {
  const currentNum = typeof current === "boolean" ? NaN : Number(current);
  const ruleNum = typeof ruleValue === "boolean" ? NaN : Number(ruleValue);
  if (!Number.isNaN(currentNum) && !Number.isNaN(ruleNum)) {
    return currentNum === ruleNum;
  }
  return String(current) === String(ruleValue);
};
